import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getWhiteLabel, updateWhiteLabel } from '@/api/white-labels';
import { getSchools } from '@/api/schools';
import { getSchoolNetworks } from '@/api/school-networks';
import type { WhiteLabel, UpdateWhiteLabelData } from '@/types/white-label';
import type { School } from '@/types/school';
import type { SchoolNetwork } from '@/types/school-network';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  Tag,
  ArrowLeft,
  Save,
  Star,
  GraduationCap,
  Network,
  ExternalLink,
  Server,
  AlertTriangle,
  Check,
} from 'lucide-react';

export function WhiteLabelDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [whiteLabel, setWhiteLabel] = useState<WhiteLabel | null>(null);
  const [schools, setSchools] = useState<School[]>([]);
  const [networks, setNetworks] = useState<SchoolNetwork[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState<UpdateWhiteLabelData>({});

  // Environment selection state for school assignment
  const [selectedSchoolId, setSelectedSchoolId] = useState<string | null>(null);
  const [selectedEnvironment, setSelectedEnvironment] = useState<'v1' | 'v2' | null>(null);
  const [migrationDialogOpen, setMigrationDialogOpen] = useState(false);

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [wlData, schoolsResponse, networksData] = await Promise.all([
        getWhiteLabel(Number(id)),
        getSchools({ limit: 1000 }),
        getSchoolNetworks(),
      ]);
      setWhiteLabel(wlData);
      setSchools(schoolsResponse.data);
      setNetworks(networksData);
      setFormData({
        name: wlData.name,
        url: wlData.url || '',
        urlAndroid: wlData.urlAndroid || '',
        urlIOS: wlData.urlIOS || '',
      });
    } catch (err) {
      setError('Erreur lors du chargement');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!whiteLabel) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await updateWhiteLabel(whiteLabel.id, formData);
      setSuccess('Marque blanche mise à jour');
      setEditMode(false);
      await fetchData();
    } catch (err) {
      setError('Erreur lors de la sauvegarde');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const doAssignSchool = async (schoolId: string, environment?: 'v1' | 'v2') => {
    if (!whiteLabel) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const payload: UpdateWhiteLabelData = {
        schoolId: schoolId === 'none' ? null : Number(schoolId),
        schoolNetworkId: null,
      };
      if (environment) {
        payload.environment = environment;
      }
      await updateWhiteLabel(whiteLabel.id, payload);
      setSuccess('École associée avec succès');
      setSelectedSchoolId(null);
      setSelectedEnvironment(null);
      await fetchData();
    } catch (err) {
      setError('Erreur lors de l\'association');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSchoolSelect = (schoolId: string) => {
    if (schoolId === 'none') {
      doAssignSchool('none');
      return;
    }

    const school = schools.find((s) => String(s.id) === schoolId);
    if (!school) return;

    const hasV1 = school.hasV1;
    const hasV2 = school.hasV2;

    if (hasV1 && hasV2) {
      // Both versions - need user to choose
      setSelectedSchoolId(schoolId);
      setSelectedEnvironment(null);
    } else if (hasV2 && !hasV1) {
      // V2 only - show migration warning
      setSelectedSchoolId(schoolId);
      setSelectedEnvironment('v2');
      setMigrationDialogOpen(true);
    } else {
      // V1 only or no deployments - just assign
      doAssignSchool(schoolId, hasV1 ? 'v1' : undefined);
    }
  };

  const handleEnvironmentConfirm = () => {
    if (!selectedSchoolId || !selectedEnvironment) return;

    if (selectedEnvironment === 'v2') {
      setMigrationDialogOpen(true);
    } else {
      doAssignSchool(selectedSchoolId, selectedEnvironment);
    }
  };

  const handleMigrationConfirm = () => {
    if (!selectedSchoolId) return;
    setMigrationDialogOpen(false);
    doAssignSchool(selectedSchoolId, 'v2');
  };

  const handleAssignNetwork = async (networkId: string) => {
    if (!whiteLabel) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await updateWhiteLabel(whiteLabel.id, {
        schoolNetworkId: networkId === 'none' ? null : Number(networkId),
        schoolId: null,
      });
      setSuccess('Réseau associé avec succès');
      await fetchData();
    } catch (err) {
      setError('Erreur lors de l\'association');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case 'ACTIVE':
        return <Badge variant="default" className="bg-green-100 text-green-700">Actif</Badge>;
      case 'INACTIVE':
        return <Badge variant="secondary">Inactif</Badge>;
      case 'SUSPENDED':
        return <Badge variant="destructive">Suspendu</Badge>;
      default:
        return <Badge variant="outline">-</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        Chargement...
      </div>
    );
  }

  if (!whiteLabel) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        Marque blanche non trouvée.
      </div>
    );
  }

  // Check if this is a V2 white label
  const isV2 = !!whiteLabel.v2TenantGroupId;

  // Filter schools: exclude those already associated with another white label
  const availableSchools = schools.filter((school) => {
    if (school.whiteLabelEntity && school.whiteLabelEntity.id !== whiteLabel.id) {
      return false;
    }
    return true;
  });

  // Get the selected school for environment choice
  const selectedSchool = selectedSchoolId
    ? schools.find((s) => String(s.id) === selectedSchoolId)
    : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/white-labels')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Tag className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold tracking-tight text-foreground">
                  {whiteLabel.name}
                </h1>
                {whiteLabel.isDefault && (
                  <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
                    <Star className="h-3 w-3 mr-1" />
                    Défaut
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                ID: {whiteLabel.id}
              </p>
            </div>
          </div>
        </div>
        {!editMode && (
          <Button onClick={() => setEditMode(true)}>
            Modifier
          </Button>
        )}
      </div>

      {/* Messages */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
          <button onClick={() => setError(null)} className="ml-2">&times;</button>
        </div>
      )}
      {success && (
        <div className="rounded-lg bg-green-50 border border-green-200 p-4 text-green-700">
          {success}
          <button onClick={() => setSuccess(null)} className="ml-2">&times;</button>
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* V2 Info - only show for V2 white labels */}
        {isV2 ? (
          <div className="rounded-lg border bg-card p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Server className="h-5 w-5 text-muted-foreground" />
              <h2 className="font-semibold text-lg">Tenant Group V2</h2>
            </div>

            <div className="grid gap-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Statut</span>
                {getStatusBadge(whiteLabel.v2Status)}
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Host</span>
                <span className="font-mono text-sm">{whiteLabel.v2Host || '-'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Tenant Group ID</span>
                <span className="font-mono text-xs truncate max-w-[200px]" title={whiteLabel.v2TenantGroupId || ''}>
                  {whiteLabel.v2TenantGroupId || '-'}
                </span>
              </div>
              {whiteLabel.tenantCount !== undefined && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Nb. Tenants</span>
                  <span className="font-medium">{whiteLabel.tenantCount}</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="rounded-lg border bg-card p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Tag className="h-5 w-5 text-muted-foreground" />
              <h2 className="font-semibold text-lg">Marque blanche V1</h2>
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              <p>
                Cette marque blanche V1 est gérée depuis le formulaire de l'école associée.
              </p>
            </div>
          </div>
        )}

        {/* URLs */}
        <div className="rounded-lg border bg-card p-6 space-y-4">
          <h2 className="font-semibold text-lg">URLs</h2>

          {editMode ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="url">URL Web</Label>
                <Input
                  id="url"
                  type="url"
                  value={formData.url || ''}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="urlAndroid">URL Android</Label>
                <Input
                  id="urlAndroid"
                  type="url"
                  value={formData.urlAndroid || ''}
                  onChange={(e) => setFormData({ ...formData, urlAndroid: e.target.value })}
                  placeholder="https://play.google.com/..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="urlIOS">URL iOS</Label>
                <Input
                  id="urlIOS"
                  type="url"
                  value={formData.urlIOS || ''}
                  onChange={(e) => setFormData({ ...formData, urlIOS: e.target.value })}
                  placeholder="https://apps.apple.com/..."
                />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEditMode(false)}>
                  Annuler
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid gap-3">
              <div>
                <span className="text-sm text-muted-foreground">Web</span>
                {whiteLabel.url ? (
                  <a
                    href={whiteLabel.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-primary hover:underline"
                  >
                    {whiteLabel.url}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <p className="text-muted-foreground">-</p>
                )}
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Android</span>
                {whiteLabel.urlAndroid ? (
                  <a
                    href={whiteLabel.urlAndroid}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-primary hover:underline truncate"
                  >
                    {whiteLabel.urlAndroid}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <p className="text-muted-foreground">-</p>
                )}
              </div>
              <div>
                <span className="text-sm text-muted-foreground">iOS</span>
                {whiteLabel.urlIOS ? (
                  <a
                    href={whiteLabel.urlIOS}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-primary hover:underline truncate"
                  >
                    {whiteLabel.urlIOS}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <p className="text-muted-foreground">-</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Association */}
        <div className="rounded-lg border bg-card p-6 space-y-4 md:col-span-2">
          <h2 className="font-semibold text-lg">Association</h2>

          {whiteLabel.schoolNetwork ? (
            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
              <Badge variant="outline" className="bg-green-100 text-green-700 border-green-200">
                <Network className="h-3 w-3 mr-1" />
                Réseau
              </Badge>
              <Link
                to={`/school-networks/${whiteLabel.schoolNetwork.id}`}
                className="font-medium hover:underline text-primary"
              >
                {whiteLabel.schoolNetwork.name}
              </Link>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleAssignNetwork('none')}
                disabled={saving}
              >
                Dissocier
              </Button>
            </div>
          ) : whiteLabel.school ? (
            <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
              <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-200">
                <GraduationCap className="h-3 w-3 mr-1" />
                École
              </Badge>
              <Link
                to={`/schools/${whiteLabel.school.code}`}
                className="font-medium hover:underline text-primary"
              >
                {whiteLabel.school.name} ({whiteLabel.school.code})
              </Link>
              {whiteLabel.activeEnvironment && (
                <Badge
                  variant="outline"
                  className={
                    whiteLabel.activeEnvironment === 'v2'
                      ? 'bg-purple-50 text-purple-700 border-purple-200'
                      : 'bg-gray-50 text-gray-700 border-gray-200'
                  }
                >
                  {whiteLabel.activeEnvironment.toUpperCase()}
                </Badge>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => doAssignSchool('none')}
                disabled={saving}
              >
                Dissocier
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-muted-foreground">
                Cette marque blanche n'est pas encore associée à une école ou un réseau.
              </p>

              {isV2 && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <p>
                    <strong>Attention :</strong> Une école ne peut pas avoir à la fois une marque blanche V1 et V2.
                    Les écoles déjà associées à une marque blanche V1 ne sont pas disponibles.
                  </p>
                </div>
              )}

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Associer à une école</Label>
                  <Select onValueChange={handleSchoolSelect} disabled={saving}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une école..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableSchools.map((school) => (
                        <SelectItem key={school.id} value={String(school.id)}>
                          {school.name} ({school.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Environment choice for dual-version school */}
                  {selectedSchool && selectedSchool.hasV1 && selectedSchool.hasV2 && (
                    <div className="space-y-2 mt-3">
                      <Label>Environnement pour {selectedSchool.name}</Label>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setSelectedEnvironment('v1')}
                          className={cn(
                            'flex-1 flex items-center gap-2 rounded-lg border-2 p-3 text-sm transition-colors',
                            selectedEnvironment === 'v1'
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-muted-foreground/50',
                          )}
                        >
                          <div
                            className={cn(
                              'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                              selectedEnvironment === 'v1'
                                ? 'bg-primary border-primary text-primary-foreground'
                                : 'border-muted-foreground/30',
                            )}
                          >
                            {selectedEnvironment === 'v1' && <Check className="h-3 w-3" />}
                          </div>
                          <div>
                            <span className="font-medium">V1</span>
                            <span className="text-muted-foreground ml-1">(legacy)</span>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedEnvironment('v2')}
                          className={cn(
                            'flex-1 flex items-center gap-2 rounded-lg border-2 p-3 text-sm transition-colors',
                            selectedEnvironment === 'v2'
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-muted-foreground/50',
                          )}
                        >
                          <div
                            className={cn(
                              'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                              selectedEnvironment === 'v2'
                                ? 'bg-primary border-primary text-primary-foreground'
                                : 'border-muted-foreground/30',
                            )}
                          >
                            {selectedEnvironment === 'v2' && <Check className="h-3 w-3" />}
                          </div>
                          <div>
                            <span className="font-medium">V2</span>
                            <span className="text-muted-foreground ml-1">(tenant group)</span>
                          </div>
                        </button>
                      </div>
                      <Button
                        onClick={handleEnvironmentConfirm}
                        disabled={!selectedEnvironment || saving}
                        className="mt-2"
                        size="sm"
                      >
                        Associer
                      </Button>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Associer à un réseau</Label>
                  <Select onValueChange={handleAssignNetwork} disabled={saving}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un réseau..." />
                    </SelectTrigger>
                    <SelectContent>
                      {networks.map((network) => (
                        <SelectItem key={network.id} value={String(network.id)}>
                          {network.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* V2 Migration Warning Dialog */}
      <Dialog open={migrationDialogOpen} onOpenChange={setMigrationDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <DialogTitle>Migration vers la marque blanche V2</DialogTitle>
            </div>
            <DialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  En activant cette marque blanche sur l'environnement V2,{' '}
                  <strong>tous les utilisateurs</strong> (parents, enseignants, directeurs) de cette
                  école devront obligatoirement se connecter via le domaine de la marque blanche
                  {whiteLabel?.v2Host && (
                    <>
                      {' : '}
                      <strong className="font-mono">{whiteLabel.v2Host}</strong>
                    </>
                  )}
                  .
                </p>
                <p>
                  Ils ne pourront plus se connecter via l'URL par défaut École Futée.
                </p>
                <p className="text-amber-700 font-medium">
                  Cette opération déclenche une migration des comptes utilisateurs vers le nouveau
                  tenant group.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setMigrationDialogOpen(false)}
            >
              Annuler
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleMigrationConfirm}
            >
              Confirmer la migration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
